import React, { useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { useGetProfilesForCaseQuery } from "../../graphql/supergraphHooks";
import { type AmlEntityTypeQuestion } from "../../graphql/supergraphTypes";
import { resolvedAccountType } from "../../utils/account_types";
import { isAmlProgram } from "../../utils/compliance_program";
import { EntityForm } from "../EntityForm";
import { EntityProfile } from "../EntityForm/types";
import { EntitySurveyPage } from "../EntitySurveyPage";
import { Question } from "../EntityTypeSurvey/types";
import { useUpdateProfile } from "../profile/hooks";
import { provideLegalName } from "../ProfileTree/utils";
import {
  prepareInstitutionPayload,
  provideDefaultQuestionIds,
  provideQuestionsWithAnswers,
} from "./utils";

export const InstitutionEditScreen = () => {
  const { caseId, basePath, programId, rootProfileId, skipDetailsForRootProfile } =
    useComplianceCaseContext();
  const { profileId } = useParams<{ profileId: string }>();
  const history = useHistory();
  const [defaultQuestions, setDefaultQuestions] = React.useState<number[]>([]);

  const questionsResult = useGetProfilesForCaseQuery({
    variables: { caseId: caseId || 0 },
    fetchPolicy: "no-cache",
    skip: !caseId,
  });

  const isRoot = parseInt(profileId) === rootProfileId;
  const allProfiles = questionsResult.data?.complianceCase?.profiles.concat(
    questionsResult.data?.complianceCase?.investingProfile,
  );
  const profile = allProfiles?.find((p) => p?.id === parseInt(profileId));
  const parentProfileId = profile?.parentRelationships?.[0]?.parentProfileId;

  const questionResponses: Array<AmlEntityTypeQuestion> | undefined =
    profile?.amlEntityTypeQuestions;

  const computedDefaultQuestions = useMemo(
    () => provideDefaultQuestionIds(questionResponses || []),
    [questionResponses],
  );

  const nonDefaultQuestionsWithAnswers = provideQuestionsWithAnswers(
    questionResponses || [],
    profile?.amlEntityTypeId || null,
  );

  const getAmlEntityIdFromSurvey = (surveyValues: { updatedQuestions: Question[] }) => {
    const positiveAnswers = surveyValues.updatedQuestions.filter(
      (question) => question.answer === "Yes",
    );

    const amlEntityId =
      positiveAnswers.length > 0
        ? positiveAnswers[0]?.targetAmlEntityTypeId
        : (defaultQuestions?.[0] ?? computedDefaultQuestions[0]);

    return amlEntityId;
  };

  const closeScreen = useCallback(() => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  }, [history]);

  const { updateInstitution, isUpdating } = useUpdateProfile(
    Number(profileId),
    Number(parentProfileId),
    closeScreen,
  );

  // Combined handler that can work for both form submission and survey completion
  const handleFormSubmit = async (values: EntityProfile) => {
    try {
      // Determine the AML entity type ID from survey if provided
      const amlEntityId =
        values.updatedQuestions && values.updatedQuestions.length > 0
          ? getAmlEntityIdFromSurvey({ updatedQuestions: values.updatedQuestions })
          : (defaultQuestions[0] ?? computedDefaultQuestions[0]);

      await updateInstitution(prepareInstitutionPayload(caseId, values, profile, amlEntityId));
    } catch {
      toast.error(`Error updating institution profile`);
    }
  };

  const handleDefaultQuestionsChange = useCallback((newDefaultQuestions: number[]) => {
    setDefaultQuestions(newDefaultQuestions);
  }, []);

  const initialValues: EntityProfile = {
    legalBusinessName: profile?.legalName || "",
    entityType: resolvedAccountType(profile) || null,
    dateOfFormation: profile?.dateOfOrganization
      ? new Date(String(profile.dateOfOrganization))
      : null,
    identificationNumber: profile?.taxIdUnencrypted || "",
    streetLine1: profile?.addresses?.[0]?.street || "",
    streetLine2: profile?.addresses?.[0]?.street2 || "",
    city: profile?.addresses?.[0]?.city || "",
    state: profile?.addresses?.[0]?.state?.code || "",
    province: profile?.addresses?.[0]?.province || "",
    zip: profile?.addresses?.[0]?.postalCode || "",
    country: profile?.addresses?.[0]?.country?.alpha2code || "",
    updatedQuestions: nonDefaultQuestionsWithAnswers || [],
  };

  const showEntityForm = isRoot ? !skipDetailsForRootProfile : isAmlProgram(programId);

  return (
    <QueryLoader result={questionsResult}>
      {showEntityForm ? (
        <EntityForm
          initialValues={initialValues}
          isUpdating={isUpdating}
          onSubmit={handleFormSubmit}
          onBack={closeScreen}
          profileId={parseInt(profileId)}
          amlEntityTypeId={profile?.amlEntityTypeId || null}
          onDefaultQuestionsChange={handleDefaultQuestionsChange}
        />
      ) : (
        <EntitySurveyPage
          questions={nonDefaultQuestionsWithAnswers || []}
          entityName={provideLegalName(profile)}
          isUpdating={isUpdating}
          onCompleted={(surveyValues) =>
            handleFormSubmit({
              ...initialValues,
              updatedQuestions: surveyValues.updatedQuestions,
            })
          }
          onBack={closeScreen}
        />
      )}
    </QueryLoader>
  );
};
